import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";

import { ROUTES } from "../../_constant/screens";
import { LayoutType } from "../../_constant/constants";

import { addCustomerIdToUrl, isRecurring } from "../../_utils/utils";
import { formatDateWithoutTZ } from "../../_utils/date";

import * as statusesConfig from "../../../configs/statuses-config.json";

import { Box, Grid, Typography } from "@mui/material";

import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { RemoveCircleOutlineOutlined } from "@mui/icons-material";

import { LoaderCircle } from "../../components/Loader";
import AssignmentActivityContainer from "../../components/AssigmentContainer/AssignmentActivityContainer";
import { CardContainer, CardParent } from "../../components/Cards/styled/card.styled";
import MoreAction from "../../components/MoreAction/MoreAction";
import { ActivityItemDate } from "../../containers/activities/_components/item/ActivityItemDate";
import DeleteActivityPopup from "../../containers/activities/_components/form/DeleteActivityPopup";
import { removeActivityFromProjectTabCache } from "../ActivitiesTabProject/removeActivityFromProjectTabCache";
import { useApolloClient } from "@apollo/client";

import {
  ControlActivityButton,
  StyledGrid,
  RemoveLabel,
  RepeatItemContainer,
} from "./ProjectTabActivityItem.style";

const { EMPTY, IN_PROGRESS, FILL } = statusesConfig["statusNameMap"];

interface ProjectTabActivityItemProps {
  activity: any; // ActivityModelShort | ActivityModelRelated
  dateType: string;
  layout: LayoutType;
  status?: string;
  disableNavigationByClick?: boolean;
  style?: {};
  isAccountPersonal: boolean;
  isDraggable?: boolean;
  isAssignmentsVisibile: boolean;
  customerId?: string | null;
  finishedView?: boolean;
  withMoreAction?: boolean;
  withDragAndDropIcon?: boolean;
  projectId?: string | null;
  removeActivity: (activityId: string) => void;
  isCustomize: boolean;
  removeActivityFromProjectWithDelete?: (activityId: string) => void;
  isHover?: boolean;
}

const ProjectTabActivityItem: React.FC<ProjectTabActivityItemProps> = (props) => {
  const {
    activity,
    dateType,
    status = "",
    disableNavigationByClick = false,
    style,
    isAccountPersonal,
    isDraggable = false,
    customerId = null,
    finishedView = false,
    withMoreAction,
    withDragAndDropIcon,
    projectId = null,
    removeActivity,
    isCustomize = false,
    removeActivityFromProjectWithDelete,
    isHover = false,
  } = props;
  const { Id, Name, Schedule, RelatedDueDateTime, isLoading, Asset, AssignedUserId } = activity;
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const client = useApolloClient();

  const iconClassName = isRecurring({ schedule: Schedule, relatedDueDate: RelatedDueDateTime })
    ? "ActivityItem_recurring"
    : "";
  const navigate = useNavigate();

  const smUp = useMediaQuery("(min-width: 600px)", { noSsr: true });
  const mdUp = useMediaQuery("(min-width: 960px)", { noSsr: true });

  const openDeleteActivityDialog = useCallback(() => setIsDeletePopupVisible(true), []);
  const closeDeleteActivityDialog = useCallback(() => setIsDeletePopupVisible(false), []);

  const options = [
    // {
    //   text: "Duplicate",
    //   icon: <FileCopyOutlinedIcon fontSize="small" />,
    //   callback: () => {},
    // },
    {
      text: "Delete",
      icon: <DeleteOutlinedIcon fontSize="small" />,
      callback: openDeleteActivityDialog,
    },
  ];

  const goToActivity = (e: React.SyntheticEvent) => {
    e.preventDefault();
    let path = `${ROUTES.SINGLE_ACTIVITY}/${activity.Id}`;
    path = customerId ? addCustomerIdToUrl(path, customerId) : path;
    navigate(path);
  };

  const handleClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    removeActivity(Id);
    removeActivityFromProjectTabCache(client, Id, projectId);
  };

  const renderAssignment = () => {
    return !isDraggable && !isAccountPersonal ? (
      <AssignmentActivityContainer
        assignedUserId={AssignedUserId}
        activityId={activity.Id}
        withoutControl
      />
    ) : null;
  };

  return (
    <>
      <CardContainer
        padding={1}
        style={isDraggable ? style : { borderBottom: "1px solid #eeeeee" }}
        borderRadius={isDraggable ? "8px" : "0"}
        onClick={disableNavigationByClick ? undefined : goToActivity}
        withLabel={status}
        isHover={isHover}
      >
        {isLoading && (
          <div className={`ActivityItem_loader`}>
            <LoaderCircle size={"xs"} />
          </div>
        )}

        <Grid spacing={1} container wrap="nowrap" sx={{
          alignItems: "flex-start"
        }}>
          {withDragAndDropIcon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "8px 0"
              }}>
              <DragIndicatorIcon color="action" fontSize="small" />
            </Box>
          )}
          <StyledGrid container sx={{ minWidth: 0 }}>
            <Grid container direction="column" sx={{
              justifyContent: "space-between"
            }}>
              <Box sx={{
                padding: "4px 0"
              }}>
                <Typography variant="body2" component="h3">
                  {Name}
                </Typography>
              </Box>
              {Asset?.Name && (
                <Box sx={{
                  margin: "8px 0"
                }}>
                  <CardParent alignItems="start">
                    <span>FOR </span>
                    {Asset.Name}
                  </CardParent>
                </Box>
              )}
            </Grid>
          </StyledGrid>
          <StyledGrid
            container
            direction="column"
            size={mdUp ? 4 : false}
            sx={{ alignItems: "flex-end", justifyContent: "space-between", flexShrink: 0 }}
          >
            <Grid
              container
              spacing={1}
              wrap="nowrap"
              sx={{
                alignItems: "center",
                justifyContent: "flex-end"
              }}>
              <Grid>{renderAssignment()}</Grid>
              <Grid style={{ minWidth: smUp ? 110 : 0 }}>
                <ActivityItemDate
                  activityDate={formatDateWithoutTZ(activity[dateType])}
                  dateType={dateType}
                  iconOnly={!smUp}
                />
              </Grid>
              {!finishedView && (
                <RepeatItemContainer container>
                  <i className={`Icon ${iconClassName}`}>repeat</i>
                </RepeatItemContainer>
              )}
              {withMoreAction && (
                <Grid>
                  <MoreAction options={options} />
                </Grid>
              )}
            </Grid>
            {isCustomize && (
              <Grid>
                <ControlActivityButton color="primary" onClick={handleClick}>
                  <RemoveLabel>Remove</RemoveLabel>
                  <RemoveCircleOutlineOutlined />
                </ControlActivityButton>
              </Grid>
            )}
          </StyledGrid>
        </Grid>
      </CardContainer>
      {isDeletePopupVisible && (
        <DeleteActivityPopup
          activity={activity}
          isDeletePopupVisible={isDeletePopupVisible}
          handleCancel={closeDeleteActivityDialog}
          customerId={customerId}
          projectId={projectId}
          withoutRedirect
          removeActivityFromProjectWithDelete={removeActivityFromProjectWithDelete}
        />
      )}
    </>
  );
};

export default ProjectTabActivityItem;
